<template>
  <Loader v-if="!doneLoading" message="Sender invitation..." />
  <div class="chat__feed">
    <div class="chat__feed__item" v-for="post in sortedItems" :key="(post.postType || 'post') + '-' + post.id"
      :data-link="postPagePath + '?id=' + post.id">
      <div v-if="post.postType === 'post'" :postype="post.postType">
        <!-- Post actions -->
        <div v-if="canEditOrDelete(post)" class="chat__feed__actions">
          <button class="chat__feed__actions-btn icon-service-dot-menu"
            @click.stop="openKebabId = openKebabId === post.id ? null : post.id">
          </button>
          <div v-if="openKebabId === post.id" class="chat__feed__actions-menu">
            <button @click="editPostId = post.id; editPostText = post.content; openKebabId = null">
              Rediger
            </button>
            <button @click="confirmDeleteId = post.id; openKebabId = null"
              class="chat__feed__actions-menu__delete">
              Slet
            </button>
          </div>
          <div v-if="confirmDeleteId === post.id" class="chat__feed__actions-confirm">
            <span>Slet opslaget?</span>
            <button class="btn btn--xs btn__secondary" @click="deletePost(post.id)">Ja, slet</button>
            <button class="btn btn--xs btn__secondary" @click="confirmDeleteId = null">Annuller</button>
          </div>
        </div>
        <div class="chat__feed__profile" v-if="!post.isGroupMessage">
          <div class="chat__profile-img">
            <img :src="apiUrl + (post.userImageUrl || defaultUrlProfileImage)" />
          </div>
          <div class="chat__feed__profile__content">
            <p>
              <a :href="profileUrl + '?initials=' + post.userAlias" target="_blank">{{ post.userName || userName }}</a>
            </p>
            <div class="label">
              <span v-for="dept in post.departments" :key="'dept-' + dept.id" class="label__department">{{ dept.name }}</span>
              <span v-if="post.includeSubDepartments && post.departments && post.departments.length"
                class="icon-service-inherit-filled label__sub-departments-icon" title="Inkluderer underafdelinger"></span>
              <span>{{ formatRelativeTime(post.date) }}</span>
            </div>
          </div>
        </div>
        <div class="chat__feed__group-profile" v-if="post.isGroupMessage">
          <div class="chat__profile-img">
            <img :src="apiUrl + post.groupImageUrl" />
          </div>
          <div class="chat__feed__group-profile__content">
            <p>
              <a :href="apiUrl + post.groupUrl" target="_blank">{{
                post.groupName
              }}</a>
            </p>
            <div class="chat__feed__profile">
              <div class="chat__profile-img">
                <img :src="apiUrl + post.userImageUrl" />
              </div>
              <div class="chat__feed__profile__content">
                <p>
                  <a :href="apiUrl + profileUrl + '?initials=' + post.userAlias" target="_blank">{{ post.userName }}</a>
                </p>
              </div>
              <div class="label">
                <span>{{ formatRelativeTime(post.date) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="chat__feed__item__content">

          <div class="rich-text" v-show="editPostId !== post.id">
            <p class="chat__feed__item__text"
              :class="{ 'chat__feed__item__text--collapsed': isLongPost(post) && !isExpanded(post) }"
              :style="isLongPost(post) && !isExpanded(post) ? { webkitLineClamp: lineClampFor(post) } : {}"
              v-html="post.content || post.message"></p>
            <button v-if="isLongPost(post)" type="button" class="chat__feed__item__read-more"
              @click="toggleExpanded(post.id)">
              {{ isExpanded(post) ? 'Vis mindre' : 'Læs mere' }}
            </button>
          </div>
          <div class="rich-text" v-show="editPostId !== post.id">
            <!-- File-->
            <a v-for="(media, idx) in fileAttachments(post)" :key="'file-' + idx"
              :href="apiUrl + media.mediaUrl" target="_blank">
              <span class="icon-document_text"></span> {{ media.mediaName }}
            </a>
          </div>
          <div class="chat__feed__gallery" :class="galleryCountClass(post)"
            v-if="galleryAttachments(post).length">
            <div class="chat__feed__gallery__item"
              :class="{ 'chat__feed__gallery__item--video': media.mediaType === 'Video' }"
              v-for="(media, idx) in visibleGalleryImages(post)" :key="'gallery-' + idx"
              @click="openGallery(post, media)">
              <template v-if="media.mediaType === 'Video'">
                <video :src="apiUrl + media.mediaUrl" controls preload="metadata"></video>
                <button type="button" class="chat__feed__gallery__expand" @click.stop="openVideoModal(media)"
                  aria-label="Vis video i fuld størrelse">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M1 5.5V1.5C1 1.22386 1.22386 1 1.5 1H5.5M10.5 1H14.5C14.7761 1 15 1.22386 15 1.5V5.5M15 10.5V14.5C15 14.7761 14.7761 15 14.5 15H10.5M5.5 15H1.5C1.22386 15 1 14.7761 1 14.5V10.5"
                      stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
              </template>
              <img v-else :src="apiUrl + media.mediaUrl" :alt="media.mediaName" />
              <div class="chat__feed__gallery__more" v-if="isLastVisibleTile(post, idx)">
                +{{ galleryAttachments(post).length - visibleGalleryImages(post).length }}
              </div>
            </div>
          </div>
          <VideoFeed v-if="post.video != null" :video="parseVideo(post.video)" />
          <div class="editpost-wrapper" v-show="editPostId === post.id">
            <QuillEditor :options="options" :theme="snow" contentType="html" v-model:content="editPostText"
              ref="quillEditor" />
            <div class="editpost-buttons">
              <button class="btn btn__secondary editpost-savepost" @click="editPost(post.id)">
                Gem</button><button class="editpost-undo" @click="(editPostId = null), (editPostText = '')">
                Fortryd
              </button>
            </div>
          </div>
        </div>
        <!-- Interactuions -->
        <PostComments :post="post || {}" :currentUserAlias="currentUserAlias" @update-post="updatePost" />
      </div>
      <NewsPost v-if="post.postType === 'news'" :post="post" :currentUserAlias="currentUserAlias"
        @update-post="updatePost" />
    </div>
  </div>
  <div v-if="activeVideoModal" class="chat__feed__video-modal" @click.self="closeVideoModal">
    <button type="button" class="chat__feed__video-modal__close" @click="closeVideoModal" aria-label="Luk video">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1L19 19M19 1L1 19" stroke="white" stroke-width="2" stroke-linecap="round" />
      </svg>
    </button>
    <video :src="apiUrl + activeVideoModal.mediaUrl" controls autoplay></video>
  </div>
</template>

<script>
import Loader from '../loader.vue'; // justér path hvis nødvendigt

import { QuillEditor, Quill } from "@vueup/vue-quill"; // <-- vigtigt: importer også Quill
import quillEmoji from 'quill-emoji'
try {
  Quill.register('modules/emoji', quillEmoji)
} catch (e) {
  // Ignore duplicate registration or environments where Quill is already configured
}
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/da";
dayjs.extend(relativeTime);
dayjs.locale("da");

import Viewer from "viewerjs";
import "viewerjs/dist/viewer.css";

import PostComments from "./PostComments.vue";
import NewsPost from "./NewsPost.vue";
import VideoFeed from "./VideoFeed.vue";
export default {
  name: "ChatwallContent",
  components: { PostComments, NewsPost, QuillEditor, VideoFeed, Loader },
  props: {
    posts: {
      type: Object,
      required: true,
    },
    currentUserAlias: {
      type: String,
      required: true,
    },
    allowedImpersonators: {
      type: Array,
      required: true,
    },
    profileUrl: {
      type: String,
      required: true
    },
    doneLoading: {
      type: Boolean,
      required: true
    }

  },
  emits: ['update-post'],
  data() {
    return {
      localPostsData: { posts: [] },
      activeVideoModal: null,
      apiUrl: process.env.VUE_APP_API_URL,
      defaultUrlProfileImage: "",
      userName: "",
      postPagePath: "",
      isAdmin: false,
      showRelativeTime: this.showRelativeTime,
      openKebabId: null,
      confirmDeleteId: null,
      editPostId: null,
      editPostText: null,
      expandedPostIds: [],
      longPostCharLimitWithImages: 220,
      longPostCharLimitTextOnly: 400,
      maxVisibleGalleryImages: 4,
      editor: null,
      snow: "snow",
      options: {
        modules: {
          toolbar: [
            [{ header: [2, 3, false] }], // "Normal" dropdown
            ['bold', 'italic', 'underline', 'strike'], // B I U S
            ['link'], // Link
            [{ list: 'ordered' }, { list: 'bullet' }], // Numbered + bullet lists
            ['emoji'] // Emoji button
          ],
          'emoji-toolbar': true,
          'emoji-textarea': false,
          'emoji-shortname': true,
        },
        placeholder: "Skriv du tekst her...",
        theme: "snow",
      },
    };
  },
  watch: {
    posts: {
      handler(newPosts) {
        if (!newPosts) return;
        this.localPostsData.posts = newPosts.posts || [];
      },
      deep: true,
      immediate: true,
    }
  },
  computed: {
    sortedItems() {
      return Array.isArray(this.localPostsData.posts)
        ? [...this.localPostsData.posts].sort(
          (a, b) => (a.date || 0) - (b.date || 0)
        )
        : [];
    },
  },
  methods: {
    canEditOrDelete(post) {
      return post.userAlias === this.currentUserAlias
        || this.allowedImpersonators.some(item => item.userAlias === post.userAlias)
        || this.isAdmin;
    },
    handleOutsideClick() {
      this.openKebabId = null;
    },
    handleVideoModalKeydown(event) {
      if (event.key === 'Escape' && this.activeVideoModal) {
        this.closeVideoModal();
      }
    },
    parseVideo(videoString) {
      try {
        return JSON.parse(videoString);
      } catch (e) {
        return null; // fallback hvis JSON er invalid
      }
    },
    stripHtml(html) {
      if (!html) return '';
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    },
    hasGallery(post) {
      return this.galleryAttachments(post).length > 0;
    },
    lineCount(html) {
      if (!html) return 0;
      const normalized = String(html)
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|li|h[1-6]|blockquote|pre)>/gi, '\n');
      const tmp = document.createElement('div');
      tmp.innerHTML = normalized;
      const text = tmp.textContent || tmp.innerText || '';
      return text.replace(/\n+$/, '').split('\n').length;
    },
    isLongPost(post) {
      const html = post.content || post.message;
      const limit = this.hasGallery(post) ? this.longPostCharLimitWithImages : this.longPostCharLimitTextOnly;
      if (this.stripHtml(html).length > limit) return true;
      return this.lineCount(html) > this.lineClampFor(post);
    },
    lineClampFor(post) {
      return this.hasGallery(post) ? 3 : 8;
    },
    isExpanded(post) {
      return this.expandedPostIds.includes(post.id);
    },
    toggleExpanded(postId) {
      this.expandedPostIds = this.isExpanded({ id: postId })
        ? this.expandedPostIds.filter(id => id !== postId)
        : [...this.expandedPostIds, postId];
    },
    postMedia(post) {
      if (post.media && post.media.length > 0) return post.media;
      if (!post.postMediaUrl) return [];
      return [{ mediaUrl: post.postMediaUrl, mediaName: post.postMediaName, mediaType: post.postMediaType }];
    },
    imageAttachments(post) {
      return this.postMedia(post).filter(m => m.mediaUrl && m.mediaType === 'Image');
    },
    videoAttachments(post) {
      return this.postMedia(post).filter(m => m.mediaUrl && m.mediaType === 'Video');
    },
    galleryAttachments(post) {
      const media = this.postMedia(post).filter(m => m.mediaUrl && (m.mediaType === 'Image' || m.mediaType === 'Video'));
      // Videos are only reachable via the small inline gallery (no shared modal
      // navigation with images), so keep them ahead of images to guarantee
      // they always land within the visible tiles rather than behind "+N".
      return [...media].sort((a, b) => {
        if (a.mediaType === b.mediaType) return 0;
        return a.mediaType === 'Video' ? -1 : 1;
      });
    },
    fileAttachments(post) {
      return this.postMedia(post).filter(m => m.mediaUrl && m.mediaType === 'File');
    },
    visibleGalleryImages(post) {
      const media = this.galleryAttachments(post);
      // Videos are always shown in full (never hidden behind "+N", since they
      // aren't reachable via the shared image viewer) - only images are capped
      // to fill whatever slots remain. Posts with many videos simply grow taller.
      const videoCount = media.filter(m => m.mediaType === 'Video').length;
      const imageCount = media.length - videoCount;
      let imageSlots = Math.max(this.maxVisibleGalleryImages - videoCount, 0);
      // If videos alone already fill every slot but images are hidden behind
      // them, force at least one image tile through so the "+N" overlay lands
      // on a tile that can actually open the viewer, instead of a video tile.
      if (imageSlots === 0 && imageCount > 0) imageSlots = 1;
      return media.slice(0, videoCount + imageSlots);
    },
    isLastVisibleTile(post, idx) {
      const visibleCount = this.visibleGalleryImages(post).length;
      return idx === visibleCount - 1
        && this.galleryAttachments(post).length > visibleCount;
    },
    galleryCountClass(post) {
      const count = this.visibleGalleryImages(post).length;
      return 'chat__feed__gallery--count-' + (count > 4 ? '5plus' : count);
    },
    openGallery(post, media) {
      if (media.mediaType !== 'Image') return;

      const images = this.imageAttachments(post);
      const startIndex = images.findIndex(m => m.mediaUrl === media.mediaUrl);
      if (startIndex === -1) return;

      const container = document.createElement('div');
      container.style.display = 'none';
      images.forEach(m => {
        const img = document.createElement('img');
        img.src = this.apiUrl + m.mediaUrl;
        img.alt = m.mediaName || '';
        container.appendChild(img);
      });
      document.body.appendChild(container);

      const viewer = new Viewer(container, {
        backdrop: true,
        button: true,
        keyboard: true,
        viewMode: 1,
        toolbar: { zoomIn: true, zoomOut: true, prev: true, next: true },
        transition: false,
        initialViewIndex: startIndex,
        hidden() {
          viewer.destroy();
          container.remove();
        },
      });

      viewer.show();
    },
    openVideoModal(media) {
      this.activeVideoModal = media;
    },
    closeVideoModal() {
      this.activeVideoModal = null;
    },
    formatRelativeTime(date) {

      var showFromNow = dayjs(date).fromNow();
      var showExactDate = dayjs(date).format("DD. MMM YYYY kl. HH.mm");
      console.log("showRelativeTime:", this.showRelativeTime);
      console.log("showFromNow:", showFromNow);
      console.log("showExactDate:", showExactDate);
      date = this.showRelativeTime ? showFromNow : showExactDate; // Skift mellem relativ og præcis tid baseret på dine behov

      return date;
    },
    async editPost(postId) {
      try {
        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/api/MessageWall/UpdatePost`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              postId: postId,
              post: this.editPostText,
              userAlias: this.currentUserAlias
            })
          }
        );

        const result = await response.json();
        if (!response.ok) {
          throw new Error(result?.message || "Fejl under opdatering af post");
        }

        // Opdater lokalt postens tekst
        this.localPostsData.posts.forEach((post) => {
          if (post.id === postId && (post.postType || 'post') === 'post') {
            post.content = this.editPostText;
          }
        });

        this.editPostId = null;
        this.editPostText = null;

      } catch (error) {
        console.error("Fejl under opdatering af post:", error);
      }
    }
    ,
    updatePost(updatedPost) {
      const postIndex = this.localPostsData.posts.findIndex(
        (p) => p.id === updatedPost.id && (p.postType || 'post') === (updatedPost.postType || 'post')
      );
      if (postIndex !== -1) {
        // Opdater hele post-objektet
        this.localPostsData.posts.splice(postIndex, 1, { ...updatedPost });
        this.$emit("update-post", this.localPostsData);
      }
    },
    async deletePost(postId) {
      const response = await fetch(
        `${process.env.VUE_APP_API_URL}/api/MessageWall/DeletePost?postId=${postId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Fejl under sletning af opslag");
      }
      const successfull = await response.json();
      if (successfull) {
        // Kun opslaget: en nyhed kan have samme id.
        this.localPostsData.posts = this.localPostsData.posts.filter(
          (post) => !(post.id === postId && (post.postType || 'post') === 'post')
        );
        // Inform parent so the source of truth also removes it
        this.$emit('update-post', { id: postId, postType: 'post', _action: 'delete' });
        this.confirmDeleteId = null;
      }
    },
  },
  mounted() {
    const chatwallWrapper = document.querySelector(".chat-wrapper");
    this.postPagePath = chatwallWrapper.dataset.postpagepath || "";
    this.isAdmin = chatwallWrapper.dataset.isadmin === "true";
    this.showRelativeTime = chatwallWrapper.dataset.showrelativetime === "true";
    this.defaultUrlProfileImage = chatwallWrapper.dataset.userprofileimage;
    this.userName = chatwallWrapper.dataset.username;
    document.addEventListener("click", this.handleOutsideClick);
    document.addEventListener("keydown", this.handleVideoModalKeydown);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleOutsideClick);
    document.removeEventListener("keydown", this.handleVideoModalKeydown);
  }
};
</script>

<style>
.ql-container {
  height: 200px !important;
}

.bem {
  margin: 5px;
}

.chat__feed__item__text--collapsed {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.chat__feed__item__read-more {
  display: inline-block;
  padding: 0;
  margin-top: 2px;
  background: none;
  border: none;
  color: var(--color-link, #1877f2);
  font-weight: 600;
  cursor: pointer;
}

.chat__feed__item__read-more:hover {
  text-decoration: underline;
}

.chat__feed__gallery {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 1fr;
  gap: 3px;
  height: 260px;
}

.chat__feed__gallery--count-1 {
  display: flex;
  align-items: center;
  justify-content: center;
  height: auto;
  max-height: 548px;
  overflow: hidden;
  background: #000;
}

.chat__feed__gallery--count-1 .chat__feed__gallery__item {
  aspect-ratio: auto;
  overflow: hidden;
  line-height: 0;
}

.chat__feed__gallery--count-1 .chat__feed__gallery__item img,
.chat__feed__gallery--count-1 .chat__feed__gallery__item video {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 548px;
  min-width: 0;
  min-height: 0;
  object-fit: contain;
}

.chat__feed__gallery--count-3 {
  grid-template-columns: repeat(3, 1fr);
}

.chat__feed__gallery--count-4 {
  grid-template-rows: repeat(2, 1fr);
  height: 360px;
}

.chat__feed__gallery--count-5plus {
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 180px;
  grid-auto-rows: 180px;
  height: auto;
}

.chat__feed__gallery__item {
  position: relative;
  overflow: hidden;
  cursor: zoom-in;
}

.chat__feed__gallery__item--video {
  cursor: default;
}

.chat__feed__gallery__item img,
.chat__feed__gallery__item video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
}

.chat__feed__gallery__more {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-size: 1.5em;
  font-weight: 600;
}

.chat__feed__gallery__expand {
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.chat__feed__gallery__expand:hover {
  background: rgba(0, 0, 0, 0.75);
}

.chat__feed__video-modal {
  position: fixed;
  inset: 0;
  z-index: 2016;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.9);
}

.chat__feed__video-modal video {
  max-width: 90vw;
  max-height: 90vh;
}

.chat__feed__video-modal__close {
  position: fixed;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  cursor: pointer;
}

.chat__feed__video-modal__close:hover {
  background: rgba(0, 0, 0, 0.8);
}

/* Viewer.js's default close button is an 80px circle pinned half off-screen
   at the top-right corner. Shrink it and pull it fully into view so it's an
   obvious, easy-to-hit target instead of a sliver poking in from the edge. */
.viewer-button {
  top: 12px !important;
  right: 12px !important;
  width: 40px !important;
  height: 40px !important;
}

.viewer-button::before {
  bottom: 10px !important;
  left: 10px !important;
}
</style>
